import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Wallet as WalletIcon, TrendingUp, Clock, Banknote, Landmark, Loader2, Check, ShieldCheck, AlertCircle } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

const peso = (n: number) => "₱" + (Number(n) || 0).toLocaleString("en-PH");

export default function Earnings() {
  const { user, loading } = useAuth();
  const [, navigate] = useLocation();
  const [data, setData] = useState<any | null>(null);
  const [channels, setChannels] = useState<{ code: string; name: string }[]>([]);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  async function load() { setData(await api.myEarnings().catch(() => null)); }
  useEffect(() => { if (user) { load(); api.walletChannels().then((r) => setChannels(r?.channels ?? [])).catch(() => {}); } }, [user?.id]);

  if (!data) return <div className="container-x py-10 text-mut flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading earnings…</div>;

  const maxV = Math.max(1, ...data.earningsHistory.map((p: any) => p.value));

  return (
    <div className="container-x py-8 max-w-3xl">
      <h1 className="text-2xl font-extrabold text-navy flex items-center gap-2 mb-1"><Banknote className="w-6 h-6 text-brand-red" />Earnings</h1>
      <p className="text-mut text-sm mb-5">What you've earned and where your payouts go.</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Stat icon={WalletIcon} label="Available" value={peso(data.availableBalance)} accent />
        <Stat icon={Clock} label="Pending release" value={peso(data.pendingRelease)} />
        <Stat icon={TrendingUp} label="This week" value={peso(data.weeklyEarnings)} />
        <Stat icon={TrendingUp} label="This month" value={peso(data.monthlyEarnings)} />
      </div>

      <div className="card p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-navy">Earnings (last 6 months)</h3>
          <span className="text-sm text-mut">Total released: <span className="font-semibold text-navy">{peso(data.releasedTotal)}</span></span>
        </div>
        {data.earningsHistory.length === 0 ? <p className="text-sm text-mut">No earnings yet.</p> : (
          <div className="flex items-end gap-2 h-32" data-testid="earnings-chart">
            {data.earningsHistory.map((p: any, i: number) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full bg-brand-red/80 rounded-t" style={{ height: `${Math.round((p.value / maxV) * 100)}%`, minHeight: p.value > 0 ? 4 : 0 }} title={peso(p.value)} />
                <span className="text-[10px] text-mut">{p.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <PayoutAccount channels={channels} account={data.bankAccount} onSaved={load} />

      <div className="card p-5 mt-6">
        <h3 className="font-bold text-navy mb-3">Recent payments</h3>
        {data.recentPayments.length === 0 ? <p className="text-sm text-mut">No payments yet.</p> : (
          <div className="divide-y divide-line">
            {data.recentPayments.map((t: any) => (
              <div key={t.id} className="py-2 flex items-center justify-between gap-3">
                <div className="min-w-0"><div className="text-sm text-navy truncate">{t.note || (t.kind === "order_payout" ? "Order payout" : "Milestone release")}</div><div className="text-xs text-mut">{new Date(t.createdAt).toLocaleDateString()}</div></div>
                <div className="font-semibold text-brand-green shrink-0">+{peso(t.amount)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }: { icon: any; label: string; value: string; accent?: boolean }) {
  return (
    <div className={`card p-4 ${accent ? "ring-1 ring-brand-red/30" : ""}`}>
      <div className="text-xs text-mut flex items-center gap-1"><Icon className="w-3.5 h-3.5" />{label}</div>
      <div className="text-lg font-extrabold text-navy mt-1">{value}</div>
    </div>
  );
}

function PayoutAccount({ channels, account, onSaved }: { channels: { code: string; name: string }[]; account: any | null; onSaved: () => void }) {
  const [edit, setEdit] = useState(false);
  const [bankCode, setBankCode] = useState(account?.bankCode || "");
  const [accountName, setAccountName] = useState(account?.accountName || "");
  const [accountNumber, setAccountNumber] = useState("");
  const [own, setOwn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setErr(null); setMsg(null);
    if (!bankCode) { setErr("Choose a bank / e-wallet."); return; }
    if (accountName.trim().length < 2 || accountNumber.trim().length < 4) { setErr("Enter the account name and number."); return; }
    if (!own) { setErr("Please confirm you own this account."); return; }
    setBusy(true);
    try {
      const r = await api.saveBankAccount({ bankCode, accountName: accountName.trim(), accountNumber: accountNumber.trim(), ownershipConfirmed: true });
      setMsg(r.message); setEdit(false); setAccountNumber(""); setOwn(false); onSaved();
    } catch (e: any) { setErr(e?.message || "Could not save."); } finally { setBusy(false); }
  }

  return (
    <div className="card p-5" data-testid="payout-account">
      <h3 className="font-bold text-navy flex items-center gap-2 mb-1"><Landmark className="w-4 h-4" />Payout account</h3>
      <p className="text-sm text-mut mb-3">Where your cash-outs are sent. Changing it locks cash-out for 24 hours for your security.</p>
      {account && !edit ? (
        <div>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-semibold text-navy">{account.bankName}</div>
              <div className="text-sm text-mut">{account.accountName} · {account.maskedNumber}</div>
            </div>
            <button onClick={() => { setEdit(true); setBankCode(account.bankCode); setAccountName(account.accountName); }} className="btn-outline h-8 px-3 text-xs">Change</button>
          </div>
          {account.cashOutLocked && <p className="text-xs text-amber-700 mt-2 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Cash-out available {new Date(account.cashOutLockedUntil).toLocaleString()}</p>}
          {msg && <p className="text-sm text-brand-green mt-2 flex items-center gap-1"><Check className="w-4 h-4" />{msg}</p>}
        </div>
      ) : (
        <div className="space-y-2">
          <div><label className="label">Bank / e-wallet</label>
            <select className="field" value={bankCode} onChange={(e) => setBankCode(e.target.value)}>
              <option value="">Select…</option>
              {channels.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          <div><label className="label">Account name</label><input className="field" value={accountName} onChange={(e) => setAccountName(e.target.value)} placeholder="Full name on the account" /></div>
          <div><label className="label">Account / mobile number</label><input className="field" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} placeholder="Account or GCash/Maya number" /></div>
          <label className="flex items-start gap-2 text-xs text-mut"><input type="checkbox" className="mt-0.5" checked={own} onChange={(e) => setOwn(e.target.checked)} /> I confirm I own or am authorized to use this payout account.</label>
          {err && <p className="text-sm text-brand-red flex items-center gap-1"><AlertCircle className="w-4 h-4" />{err}</p>}
          <div className="flex gap-2">
            <button onClick={save} disabled={busy} className="btn-red h-9 disabled:opacity-60" data-testid="save-payout">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}Save payout account</button>
            {account && <button onClick={() => { setEdit(false); setErr(null); }} className="btn-outline h-9">Cancel</button>}
          </div>
        </div>
      )}
    </div>
  );
}
